/**
 * Barre de navigation latérale partagée entre tous les onglets.
 *
 * Compact (téléphone) : tab bar iOS-like, 4 destinations + « Plus ».
 * Wide / medium : colonne d'icônes à gauche (comportement historique).
 */

import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { useApp } from '../contexts/AppContext';
import { PlayerAvatar } from './PlayerAvatar';
import {
  HomeIcon, ChatIcon, CardsIcon, GameIcon, RankingsIcon, DiffereIcon,
  SettingsIcon, LogoutIcon, TrainingIcon, SidebarRevealIcon, LibraryIcon,
  ForumsIcon, SpaceToggleIcon, AdminShieldIcon, MoreHorizIcon, SubscriptionIcon,
  BetaFeedbackIcon,
} from './Icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSystemStatusContext } from '../contexts/SystemStatusContext';
import { isNative } from '../platform/capacitor';
import { isBetaExperienceActive } from './BetaWelcomeModal';
import type { Session } from '../types/app';
import type { TabId } from '../types/app';

interface NavSidebarProps {
  session: Session;
  onLogout: () => void;
  onToggleCompet?: () => void;
  isAdmin?: boolean;
}

interface NavEntry {
  id: TabId;
  label: string;
  title: string;
  visible: boolean;
  icon: ReactNode;
  badge?: ReactNode;
  onClick: () => void;
}

function compactPrimaryIds(compet: boolean, inGame: boolean): TabId[] {
  const ids: TabId[] = ['chat', 'tables'];
  if (inGame) {
    ids.push('game');
    ids.push(compet ? 'competitions' : 'forums');
  } else if (compet) {
    ids.push('competitions', 'training');
  } else {
    ids.push('rankings', 'forums');
  }
  return ids;
}

export default function NavSidebar({ session, onLogout, onToggleCompet, isAdmin }: NavSidebarProps) {
  const {
    activeTab,
    setActiveTab,
    gameInfo,
    chatUnreadCount,
    gameNotification,
    invitationCount,
    trainingLeftColumnHidden,
    avatarVersion,
    libraryNewCount,
    navigateToLibrary,
  } = useApp();

  const handleLibraryClick = () => {
    if (libraryNewCount > 0 && activeTab !== 'library') {
      navigateToLibrary({ section: 'shared_private' });
      return;
    }
    setActiveTab('library');
  };

  const handleTrainingClick = () => {
    setActiveTab('training');
  };

  const { isWide, isCompact } = useResponsive();
  const { status } = useSystemStatusContext();
  const showNativeBetaFeedback = isNative() && isBetaExperienceActive(status);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [settingsOverflow, setSettingsOverflow] = useState(false);

  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
      if (showMore && navRef.current && !navRef.current.contains(target)) {
        setShowMore(false);
      }
    }
    if (showUserMenu || showMore) {
      document.addEventListener('pointerdown', handlePointerDown);
      return () => document.removeEventListener('pointerdown', handlePointerDown);
    }
  }, [showUserMenu, showMore]);

  useEffect(() => {
    if (isCompact) return;
    const navEl = navRef.current;
    if (!navEl) return;

    const check = () => {
      const buttonSize = 44;
      const gap = 8;

      let navTopCount = 0;
      if (isWide) navTopCount++;
      navTopCount += 2;
      if (gameInfo) navTopCount++;
      if (session.compet) navTopCount += 3;
      navTopCount += 2;

      const navBottomCount = 2;
      const navTopSize = navTopCount * buttonSize + Math.max(0, navTopCount - 1) * gap;
      const navBottomSize = navBottomCount * buttonSize + (navBottomCount - 1) * gap;
      setSettingsOverflow(navTopSize + navBottomSize + 24 > navEl.clientHeight);
    };

    const observer = new ResizeObserver(check);
    observer.observe(navEl);
    check();
    return () => observer.disconnect();
  }, [isCompact, isWide, gameInfo, session.compet]);

  useEffect(() => {
    setShowMore(false);
  }, [activeTab]);

  const entries: NavEntry[] = [
    {
      id: 'home',
      label: 'Accueil',
      title: 'Accueil',
      visible: isWide && !isCompact,
      icon: <HomeIcon size={22} filled={activeTab === 'home'} />,
      onClick: () => setActiveTab('home'),
    },
    {
      id: 'chat',
      label: 'Chat',
      title: 'Discussions',
      visible: true,
      icon: <ChatIcon size={22} filled={activeTab === 'chat'} />,
      badge: chatUnreadCount > 0
        ? <span className="unread-badge total">{chatUnreadCount}</span>
        : undefined,
      onClick: () => setActiveTab('chat'),
    },
    {
      id: 'tables',
      label: 'Tables',
      title: 'Tables de jeu',
      visible: true,
      icon: <CardsIcon size={22} filled={activeTab === 'tables'} />,
      badge: invitationCount > 0 && activeTab !== 'tables'
        ? (
          <span
            className="unread-badge total invitation-badge"
            title={`${invitationCount} invitation${invitationCount > 1 ? 's' : ''} en attente`}
          >
            {invitationCount}
          </span>
        )
        : undefined,
      onClick: () => setActiveTab('tables'),
    },
    {
      id: 'game',
      label: 'Partie',
      title: 'Partie en cours',
      visible: Boolean(gameInfo),
      icon: <GameIcon size={22} filled={activeTab === 'game'} />,
      badge: gameNotification && activeTab !== 'game'
        ? <span className="unread-badge total game-badge" title={gameNotification.message}>!</span>
        : undefined,
      onClick: () => setActiveTab('game'),
    },
    {
      id: 'training',
      label: 'Coach',
      title: activeTab === 'training' && trainingLeftColumnHidden
        ? 'Afficher la colonne Entraînement'
        : 'Entraînement',
      visible: Boolean(session.compet),
      icon: activeTab === 'training' && trainingLeftColumnHidden
        ? <SidebarRevealIcon size={22} />
        : <TrainingIcon size={22} filled={activeTab === 'training'} />,
      onClick: handleTrainingClick,
    },
    {
      id: 'library',
      label: 'Biblio',
      title: libraryNewCount > 0 ? 'Bibliothèque — nouveaux partages' : 'Bibliothèque',
      visible: Boolean(session.compet),
      icon: <LibraryIcon size={22} filled={activeTab === 'library'} />,
      badge: libraryNewCount > 0 && activeTab !== 'library'
        ? <span className="unread-badge total">{libraryNewCount > 99 ? '99+' : libraryNewCount}</span>
        : undefined,
      onClick: handleLibraryClick,
    },
    {
      id: 'competitions',
      label: 'Compét.',
      title: 'Compétitions',
      visible: Boolean(session.compet),
      icon: <DiffereIcon size={22} filled={activeTab === 'competitions'} />,
      onClick: () => setActiveTab('competitions'),
    },
    {
      id: 'rankings',
      label: 'Classement',
      title: 'Classements',
      visible: true,
      icon: <RankingsIcon size={22} filled={activeTab === 'rankings'} />,
      onClick: () => setActiveTab('rankings'),
    },
    {
      id: 'forums',
      label: 'Forums',
      title: 'Forums',
      visible: true,
      icon: <ForumsIcon size={22} filled={activeTab === 'forums'} />,
      onClick: () => setActiveTab('forums'),
    },
    {
      id: 'admin',
      label: 'Admin',
      title: 'Administration',
      visible: Boolean(isAdmin),
      icon: <AdminShieldIcon size={22} filled={activeTab === 'admin'} />,
      onClick: () => setActiveTab('admin'),
    },
    {
      id: 'settings',
      label: 'Réglages',
      title: 'Paramètres',
      visible: true,
      icon: <SettingsIcon size={22} filled={activeTab === 'settings'} />,
      onClick: () => setActiveTab('settings'),
    },
  ];

  const primaryIds = useMemo(
    () => compactPrimaryIds(Boolean(session.compet), Boolean(gameInfo)),
    [session.compet, gameInfo],
  );

  const primaryEntries = primaryIds
    .map(id => entries.find(e => e.id === id))
    .filter((e): e is NavEntry => Boolean(e?.visible));

  const moreEntries = entries.filter(
    e => e.visible && e.id !== 'home' && e.id !== 'settings' && !primaryIds.includes(e.id),
  );
  const moreHasBadge = moreEntries.some(e => e.badge);
  const moreIsActive = moreEntries.some(e => e.id === activeTab)
    || activeTab === 'settings'
    || activeTab === 'mySubscription'
    || activeTab === 'subscription';

  const renderTabButton = (entry: NavEntry, opts?: { labeled?: boolean }) => (
    <button
      key={entry.id}
      type="button"
      className={`nav-button ${activeTab === entry.id ? 'active' : ''} ${opts?.labeled ? 'nav-button--tab' : ''}`}
      title={entry.title}
      aria-label={entry.title}
      aria-current={activeTab === entry.id ? 'page' : undefined}
      onClick={() => {
        setShowMore(false);
        entry.onClick();
      }}
    >
      <span className="nav-button-icon">
        {entry.icon}
        {entry.badge}
      </span>
      {opts?.labeled && <span className="nav-button-label">{entry.label}</span>}
    </button>
  );

  const closeMenus = () => {
    setShowUserMenu(false);
    setShowMore(false);
  };

  const accountNavItems = (
    <>
      {onToggleCompet && (
        <button
          type="button"
          role="menuitem"
          className="user-menu-item"
          onClick={() => { onToggleCompet(); closeMenus(); }}
          title={
            session.hasCompetitionAccess
              ? undefined
              : 'Aperçu gratuit de l\'espace compétition (mode visiteur)'
          }
        >
          <SpaceToggleIcon size={22} filled={false} />
          <span className="user-menu-item-label">
            Passer en {session.compet ? 'Détente' : 'Compétition'}
            {!session.hasCompetitionAccess && !session.compet && (
              <small>aperçu gratuit</small>
            )}
          </span>
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className={`user-menu-item ${activeTab === 'mySubscription' || activeTab === 'subscription' ? 'is-active' : ''}`}
        onClick={() => { setActiveTab('mySubscription'); closeMenus(); }}
      >
        <SubscriptionIcon size={22} filled={activeTab === 'mySubscription' || activeTab === 'subscription'} />
        <span>Mon abonnement</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className={`user-menu-item ${activeTab === 'settings' ? 'is-active' : ''}`}
        onClick={() => { setActiveTab('settings'); closeMenus(); }}
      >
        <SettingsIcon size={22} filled={activeTab === 'settings'} />
        <span>Paramètres</span>
      </button>
      {showNativeBetaFeedback && (
        <button
          type="button"
          role="menuitem"
          className="user-menu-item"
          onClick={() => {
            closeMenus();
            window.dispatchEvent(new CustomEvent('wt-open-beta-feedback'));
          }}
        >
          <BetaFeedbackIcon size={22} />
          <span>Feedback bêta-test</span>
        </button>
      )}
    </>
  );

  const accountFooter = (
    <div className="user-menu-account-bar">
      <PlayerAvatar pseudo={session.pseudo} size={32} cacheBust={avatarVersion || undefined} />
      <div className="user-menu-info">
        <span className="user-menu-name">{session.pseudoDisplay}</span>
        <span className="user-menu-space">
          {session.compet ? 'Compétition' : 'Détente'}
        </span>
      </div>
      <button
        type="button"
        className="user-menu-logout-btn"
        onClick={onLogout}
      >
        <LogoutIcon size={18} />
        <span>Déconnexion</span>
      </button>
    </div>
  );

  if (isCompact) {
    return (
      <nav className="nav-sidebar nav-sidebar--compact" ref={navRef}>
        <div className="nav-sidebar-blur" aria-hidden="true" />
        {showMore && (
          <button
            type="button"
            className="nav-more-backdrop"
            aria-label="Fermer le menu Plus"
            onClick={() => setShowMore(false)}
          />
        )}
        {showMore && (
          <div className="nav-more-sheet" role="menu" aria-label="Plus">
            <div className="nav-more-sheet-list">
              {moreEntries.map(entry => (
                <button
                  key={entry.id}
                  type="button"
                  role="menuitem"
                  className={`user-menu-item ${activeTab === entry.id ? 'is-active' : ''}`}
                  onClick={() => {
                    setShowMore(false);
                    entry.onClick();
                  }}
                >
                  {entry.icon}
                  <span>{entry.title}</span>
                  {entry.badge}
                </button>
              ))}
              {accountNavItems}
            </div>
            {accountFooter}
          </div>
        )}
        <div className="nav-tab-bar">
          {primaryEntries.map(entry => renderTabButton(entry, { labeled: true }))}
          <button
            type="button"
            className={`nav-button nav-button--tab ${moreIsActive || showMore ? 'active' : ''}`}
            title="Plus"
            aria-label="Plus"
            aria-expanded={showMore}
            onClick={() => setShowMore(v => !v)}
          >
            <span className="nav-button-icon">
              <MoreHorizIcon size={22} filled={moreIsActive || showMore} />
              {moreHasBadge && <span className="unread-badge total unread-badge--dot" />}
            </span>
            <span className="nav-button-label">Plus</span>
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav className="nav-sidebar" ref={navRef}>
      <div className="nav-top">
        {entries.filter(e => e.visible && e.id !== 'admin' && e.id !== 'settings').map(e => renderTabButton(e))}
      </div>

      <div className="nav-bottom">
        {isAdmin && renderTabButton(entries.find(e => e.id === 'admin')!)}
        {!settingsOverflow && renderTabButton(entries.find(e => e.id === 'settings')!)}

        <div className="user-menu-container" ref={userMenuRef}>
          <button
            type="button"
            className="nav-button user-avatar-button"
            title={session.pseudoDisplay}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <PlayerAvatar pseudo={session.pseudo} size={32} cacheBust={avatarVersion || undefined} />
          </button>
          {showUserMenu && (
            <div className="user-menu-dropdown">
              {accountNavItems}
              {accountFooter}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
